import {
  elementIsCenterOfScreen,
  convertUrlSearchParams,
  toggleSearchActive,
} from './car-search/2023/car-search-helper';


/**
 * Defines sessionStorage key where last results will be
 * saved when user clicks car card
 *
 * @type {string}
 */
const SESSION_STORAGE_KEY = 'search-watcher-last-results';

/**
 * Last paging value sessionStorage key
 * where last paging value is saved.
 *
 * @type {string}
 */
const LAST_PAGING_SESSION_STORAGE_KEY = 'search-watcher-last-paging-value';

/**
 * Adds functionality to search watcher found cars list
 */
class SearchWatcherCarList {

  /**
   * SearchWatcherCarList container
   * @type {Element}
   * @private
  */
  element;

  /**
   * If results loaded from sessionStorage this will be true
   * to indicate popstate event not to fire search function
   *
   * @type {boolean}
   * @private
   */
  #sessionStorageLoaded = false;

  /**
   * Element querySelector search strings
   * Used when constructed, loop and fill elementPartials
   *
   * @type {object}
   * @private
   */
  #elementPartialsQueryStrings = {
    loadMore: '#car-search-load-more',
    carsGrid: '.hedin-search-watcher-found-cars--list',
    noMoreResults: '#car-search-no-more-results',
    loadMoreSpinner: '.car-search-main__loader-holder',
  }

  /**
   * Maximum pages load with infinite scroll
   * until the "load more" button will set visible
   *
   * @type {number}
   * @private
   */
  #maxInfiniteLoadPages = 2;

  /**
   * Different kind of element partials inside
   * SearchWatcherCarList, such as "Load more" -buttons etc.
   *
   * @type {object}
   * @private
   */
  #elementPartials = {}

  /**
   * Different types of cards data
   *
   * @type {object}
   * @private
   */
  #cards = {
    basicCard: JSON.parse(document.getElementById('basic-card-json').innerHTML),
  }

  /**
   * Currently visible car cards length
   */
  #visibleCarsCount = 0;

  /**
   * Current cars count. Used when comparing cars count
   * and if we need to load more or not.
   *
   * @type {number}
   * @private
   */
  #currentCarsCount = 0;

  /**
   * Current page for loadMore
   *
   * @type {number}
   * @private
   */
  #currentPage = 0;

  /**
   * Last loader state saved here
   * When scrolling and e.g. loader position is fixed
   * save fixed here so we do not have to set same values
   * to loader element
   *
   * @type {string} - Available values: top, bottom, fixed
   */
  #lastLoaderState = 'top';

  /**
   * Store first and last cards elements here
   * This will avoid querySelectors inside scroll -event
   *
   * @type {object}
   * @private
   */
  #firstAndLastCardElements = {
    first: null,
    last: null
  }

  /**
   * Defines if search app is currently loading new cars
   *
   * @type {boolean}
   * @private
   */
  #isLoading = false;

  /**
   * Abort controller to handle ajax-requests aborts
   *
   * @type {AbortController|boolean} - Default is false
   */
  abortController = false

  /**
   * Constructor. Starts everything up if accordion elements are found
   * @param {HTMLElement} element element to bind accordion item functionality
   */
  constructor(element) {
    this.element = element;

    // Fill elementPartials member
    for (const [elementName, queryString] of Object.entries(this.#elementPartialsQueryStrings)) {
      this.#elementPartials[elementName] = this.element.querySelector(queryString);
    }

    // Get current cars count, parse window.currentPaging
    // window.currentPaging is printed straight to the dom
    this.#currentCarsCount = +JSON.parse(window.currentPaging).totalResults;

    this.#visibleCarsCount = this.#elementPartials.carsGrid.querySelectorAll('.card--basic-card').length;

    // Bind element events
    this.#bindEvents();

    this.#observeLoadMoreButton();
    this.#handleNoMoreResultsLeft();

    // Set first and last cards position info
    this.#setFirstAndLastCardsPositionInfo();
    // Set loader initial top value
    this.#setLoaderInitialTopValue();
  }

  /**
   * Search cars and update filter tags.
   * @param {object}  options    - Options object
   * @param {boolean} doNotHandleModelDropdown - If set true, function will not update models dropdown
   *                                             e.g. in case of model dropdown is handled earlier (free text search)
   */
  async searchCars(options = {}) {
    this.toggleLoadingAnimation = true;

    // Force calculate position for spinner
    this.#calculateLoadingSpinnerPosition(true);

    // Abort possible last request
    this.#abortSearch();
    this.abortController = new AbortController();

    if (window.searchParams) {
      const searchParamsObj = window.searchParams;
      searchParamsObj.page = this.#currentPage;
      searchParamsObj.order = 'last_updated_desc';
      const ajaxParams = convertUrlSearchParams(searchParamsObj, false, this.#currentPage);

      try {
        await fetch(`/stage-ajax/2022-car-search/?${ajaxParams}`, {
          signal: this.abortController.signal
        })
          .then(response => response.json())
          .then(response => {
            this.toggleLoadingAnimation = false;
            this.#renderData(response.vehicles);

            // When not loading more set load more button invsible
            if (!options.doNotSaveHistory) {
              this.#elementPartials.loadMore.classList.remove('car-search-main__load-more-button--in');
            }

            // Save only GT-X cars count to avoid load nothing when
            // search results ends
            this.#currentCarsCount = response.paging.totalResults;

            // Calculate visible cars count
            this.#visibleCarsCount = this.#elementPartials.carsGrid.querySelectorAll('.card--basic-card').length;
            this.#handleNoMoreResultsLeft();

            // Get first and last cards top and scrollheight
            this.#setFirstAndLastCardsPositionInfo();
          }).catch(() => { });
      } catch (e) {
        console.warn('Search aborted', e);
      }
    }
  }

  /**
   * Set loading state, if loading set loader
   * visible and fade search container
   *
   * @param {boolean} value - True if loading, otherwise false
   */
  set toggleLoadingAnimation(value) {
    toggleSearchActive(value);
    this.#isLoading = value;
  }

  /**
   * Render data
   * @param {object[]} nwCars       - Vehicles array from server
   * @param {object[]} factoryCars  - Factory car groups from server
   * @param {object[]} liftCards    - Lift cards from server {title: '', text: '', buttonText: '', buttonUrl: ''
   * @param {boolean} append        - Append vehicles'
   * @param {object}   carSearchSeo - Render car search seo texts to bottom of page
   */
  #renderData(nwCars) {

    const carsMarkup = nwCars.map(car => {
      const customClasses = `${this.#cards.basicCard.cardCustomClasses}${car.labels.length ? ' card--label' : ''}`;
      return window.Dali.render(this.#cards.basicCard.template, {
        ...car,
        cardCustomClasses: customClasses
      }, this.#cards.basicCard.partials);

    });

    document.querySelector('.car-search-grid').innerHTML += carsMarkup.join('');
  }

  /**
   * Aborts current search if search is running
   */
  #abortSearch() {
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  /**
   * Bind events to necessary inputs
   *
   * @private
   */
  #bindEvents() {
    // Load more event
    this.#elementPartials.loadMore.addEventListener('click', this.#loadMore.bind(this));

    window.addEventListener('popstate', this.#handlePopState.bind(this));

    // Scroll & resize events
    ['scroll', 'resize'].forEach(eventListenerType => window.addEventListener(eventListenerType, () =>
      this.#calculateLoadingSpinnerPosition(eventListenerType === 'resize'),
    { passive: true })
    );
  }

  /**
   * Calculate spinner position in viewport
   *
   * @param {boolean} forceCalculate - Ignore "isLoading" and "lastLoaderState"
   * @private
   */
  #calculateLoadingSpinnerPosition(forceCalculate = false) {
    if (this.#isLoading || forceCalculate) {
      const firstCardOverHalf = elementIsCenterOfScreen(this.#firstAndLastCardElements.first);
      const lastCardOverHalf = elementIsCenterOfScreen(this.#firstAndLastCardElements.last);

      // First card and last card has not crossed center of screen --> top
      if (!firstCardOverHalf && !lastCardOverHalf && (this.#lastLoaderState !== 'top' || forceCalculate)) {
        this.#lastLoaderState = 'top';
        this.#elementPartials.loadMoreSpinner.classList.remove('car-search-main__loader-holder--fixed');
        // Get offset top value from
        this.#setLoaderInitialTopValue();
      }

      // First card has crossed center of screen --> scrolling
      if (firstCardOverHalf && !lastCardOverHalf && (this.#lastLoaderState !== 'fixed' || forceCalculate)) {
        this.#lastLoaderState = 'fixed';
        this.#elementPartials.loadMoreSpinner.style.top = '';
        this.#elementPartials.loadMoreSpinner.classList.add('car-search-main__loader-holder--fixed');
      }

      // Last card has crossed center of screen --> bottom
      if (firstCardOverHalf && lastCardOverHalf && (this.#lastLoaderState !== 'bottom' || forceCalculate)) {
        this.#lastLoaderState = 'bottom';
        this.#elementPartials.loadMoreSpinner.classList.remove('car-search-main__loader-holder--fixed');
        this.#elementPartials.loadMoreSpinner.style.top = `${this.#firstAndLastCardElements.last.offsetTop +
          (this.#firstAndLastCardElements.last.scrollHeight / 2) -
          (this.#elementPartials.loadMoreSpinner.scrollHeight / 2)}px`;
      }
    }
  }

  /**
   * Handle popstate when user hits browser's back or forward button
   *
   * @param {Event} event Popstate event
   */
  async #handlePopState(event) {
    // Check that URL does not contain hash
    if (window.location.hash === '' && !this.#sessionStorageLoaded && Object.keys(event.state).length) {
      this.toggleLoadingAnimation = true;

      // Set sessionStorageLoaded to its initial value
      this.#sessionStorageLoaded = false;
      this.searchCars({ doNotSaveHistory: true, doNotHandleModelDropdown: true });
    }
  }

  #handleNoMoreResultsLeft() {
    if (this.#currentCarsCount <= this.#visibleCarsCount) {
      this.#elementPartials.noMoreResults.classList.add('no-more-results--in');

      // Hide load more if exists
      this.#elementPartials.loadMore.classList.remove('car-search-main__load-more-button--in');
    } else {
      this.#elementPartials.noMoreResults.classList.remove('no-more-results--in');
    }
  }

  /**
   * Load more button click event
   *
   * @param {Event} event - Binded event
   */
  #loadMore(event) {
    event.preventDefault();
    if (this.#currentPage === this.#maxInfiniteLoadPages) {
      this.#elementPartials.loadMore.classList.add('car-search-main__load-more-button--in');
    }
    this.#currentPage += 1;
    this.searchCars({ loadMore: true, doNotSaveHistory: true });
  }

  /**
   * Observer for load more button when user
   * scrolls button to viewport, execute load more
   */
  #observeLoadMoreButton() {
    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.#currentPage <= this.#maxInfiniteLoadPages && this.#currentCarsCount > this.#visibleCarsCount) {
          this.#elementPartials.loadMore.click();
        }
      });
    }, { threshold: 1 });

    observer.observe(this.#elementPartials.loadMore);
  }

  /**
   * Set loader initial top value
   *  - Get first card on dom
   *  - Get bounding client rect top value
   *  - Get car scrollHeight / 2
   *  - Set loader top value: bouningClientRect.top + scrollHeight / 2
   */
  #setLoaderInitialTopValue() {
    const firstCard = this.#firstAndLastCardElements.first;
    this.#elementPartials.loadMoreSpinner.style.top = `${(firstCard.scrollHeight / 2) + firstCard.offsetTop - (this.#elementPartials.loadMoreSpinner.scrollHeight / 2)}px`;
  }

  /**
   * Set first and last car cards top- and scrollHeight -value
   *
   * @private
   */
  #setFirstAndLastCardsPositionInfo() {
    const [firstCard, lastCard] = this.#elementPartials.carsGrid.querySelectorAll('.card:first-child, .card:last-child');

    // If first or last card does not exists, fallback to carsGrid
    // to avoid javascript error when calculating spinner position
    if (!firstCard || !lastCard) {
      this.#firstAndLastCardElements.first = this.#elementPartials.carsGrid;
      this.#firstAndLastCardElements.last = this.#elementPartials.carsGrid;
    } else {
      this.#firstAndLastCardElements.first = firstCard;
      this.#firstAndLastCardElements.last = lastCard;
    }
  }

  /**
   * init Static initializer of the class.
   *
   * @static
   */
  static init() {
    const searchWatcherCarList = document.querySelector('.hedin-search-watcher-found-cars');

    if (searchWatcherCarList) {
      new SearchWatcherCarList(searchWatcherCarList);
    }
  }
}

export default SearchWatcherCarList;

export {
  SESSION_STORAGE_KEY,
  LAST_PAGING_SESSION_STORAGE_KEY
}
